import { getArtworkMetadata } from "@packages/sdk";
import type { RefObject } from "react";
import { useCallback, useMemo } from "react";

import {
  getContentAlbum,
  getContentArtist,
  getContentImage,
  getContentTitle,
} from "../lib/contentIdentification";
import type { Display } from "../providers/MediaProvider/MediaProvider";
import type { MediaElementsRef } from "../types/MediaElementsRef.types";
import { useCurrentQueueItem } from "./useCurrentQueueItem";

const supportsMediaSession = () =>
  typeof navigator !== "undefined" && "mediaSession" in navigator;

type UseMediaSessionAPIProps = {
  handleNext: () => Promise<unknown>;
  handleReverse: () => Promise<void>;
  mediaRef: RefObject<MediaElementsRef>;
};

export type UseMediaSessionAPIReturn = {
  updateMetadata: () => void;
  updatePlaybackState: (paused: boolean, display: Display) => void;
  establishStandardHandlers: (
    play: MediaElementsRef["play"],
    pause: (wasNotTapped?: boolean) => void,
    onClose: () => void,
    noSeek?: boolean,
  ) => void;
  destroyStandardHandlers: () => void;
  establishSeekHandlers: () => void;
  destroySeekHandlers: () => void;
  establishTrackHandlers: () => void;
  destroyTrackHandlers: () => void;
  updateMediaPosition: () => void;
  updateSpeed: () => void;
  updateDuration: () => void;
};

export const useMediaSessionAPI = ({
  handleNext,
  handleReverse,
  mediaRef,
}: UseMediaSessionAPIProps): UseMediaSessionAPIReturn => {
  const { currentItem } = useCurrentQueueItem();

  const updateMetadata = useCallback(() => {
    if (currentItem && supportsMediaSession()) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: getContentTitle(currentItem),
        artist: getContentArtist(currentItem),
        album: getContentAlbum(currentItem),
        artwork: getArtworkMetadata(getContentImage(currentItem)),
      });
    }
  }, [currentItem]);

  const updatePlaybackState = (paused: boolean, display: Display) => {
    if (supportsMediaSession()) {
      navigator.mediaSession.playbackState =
        display === "hidden" ? "none" : paused ? "paused" : "playing";
    }
  };

  const establishStandardHandlers = (
    play: () => Promise<void>,
    pause: (wasNotTapped?: boolean) => void,
    onClose: () => void,
    noSeek: boolean = false,
  ) => {
    if (supportsMediaSession()) {
      navigator.mediaSession.setActionHandler("play", () => play());
      navigator.mediaSession.setActionHandler("pause", () => pause());
      navigator.mediaSession.setActionHandler("stop", () => {
        pause(true);
        if (mediaRef.current) mediaRef.current.currentTime = 0;
        onClose();
      });
      if (!noSeek) {
        navigator.mediaSession.setActionHandler("seekto", ({ seekTime }) => {
          if (mediaRef.current) {
            mediaRef.current.currentTime = seekTime;
          }
        });
      }
    }
  };

  const destroyStandardHandlers = () => {
    if (supportsMediaSession()) {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
      navigator.mediaSession.setActionHandler("seekto", null);
      navigator.mediaSession.setActionHandler("stop", null);
    }
  };

  const establishSeekHandlers = () => {
    if (supportsMediaSession()) {
      navigator.mediaSession.setActionHandler(
        "seekbackward",
        ({ seekOffset }) => {
          if (mediaRef.current) {
            mediaRef.current.currentTime = Math.max(
              mediaRef.current.currentTime - (seekOffset ?? 10),
              0,
            );
          }
        },
      );
      navigator.mediaSession.setActionHandler(
        "seekforward",
        ({ seekOffset }) => {
          if (mediaRef.current) {
            mediaRef.current.currentTime = Math.min(
              mediaRef.current.currentTime + (seekOffset ?? 10),
              mediaRef.current.duration,
            );
          }
        },
      );
    }
  };

  const destroySeekHandlers = () => {
    if (supportsMediaSession()) {
      navigator.mediaSession.setActionHandler("seekforward", null);
      navigator.mediaSession.setActionHandler("seekbackward", null);
    }
  };

  const establishTrackHandlers = useCallback(() => {
    if (supportsMediaSession()) {
      navigator.mediaSession.setActionHandler("nexttrack", () => {
        void handleNext();
      });
      navigator.mediaSession.setActionHandler("previoustrack", () =>
        handleReverse(),
      );
    }
  }, [handleNext, handleReverse]);

  const destroyTrackHandlers = () => {
    if (supportsMediaSession()) {
      navigator.mediaSession.setActionHandler("nexttrack", null);
      navigator.mediaSession.setActionHandler("previoustrack", null);
    }
  };

  const updateDuration = useCallback(() => {
    if (supportsMediaSession() && currentItem) {
      pushState();
    }
  }, [currentItem]);

  const updateSpeed = useCallback(() => {
    if (supportsMediaSession() && currentItem) {
      pushState();
    }
  }, [currentItem]);

  const updateMediaPosition = useCallback(() => {
    if (supportsMediaSession() && currentItem) {
      pushState();
    }
  }, [currentItem]);

  const pushState = () => {
    if (
      !isNaN(mediaRef.current?.duration) &&
      mediaRef.current?.duration !== 0 &&
      !isNaN(mediaRef.current?.currentTime) &&
      !isNaN(mediaRef.current?.playbackRate) &&
      mediaRef.current?.playbackRate !== 0
    ) {
      navigator.mediaSession.setPositionState({
        position: Math.min(
          mediaRef.current.duration,
          mediaRef.current.currentTime,
        ),
        playbackRate: mediaRef.current.playbackRate,
        duration: mediaRef.current.duration,
      });
    }
  };

  return useMemo(
    () => ({
      updateMediaPosition,
      updateMetadata,
      updatePlaybackState,
      establishSeekHandlers,
      establishStandardHandlers,
      establishTrackHandlers,
      destroySeekHandlers,
      destroyStandardHandlers,
      destroyTrackHandlers,
      updateDuration,
      updateSpeed,
    }),
    [
      updateMediaPosition,
      updateMetadata,
      updatePlaybackState,
      establishSeekHandlers,
      establishStandardHandlers,
      establishTrackHandlers,
      destroySeekHandlers,
      destroyStandardHandlers,
      destroyTrackHandlers,
      updateDuration,
      updateSpeed,
    ],
  );
};
